import { useState, useEffect, useCallback } from "react"
import useLocalStorage from "@kit/design/use-localstorage"

const POLLING_INTERVAL_MS = 5000

const isActive = status => status === "started" || status === "processing"

export function useBulkNotifications() {
  const [topics, setTopics] = useState([])
  const [seenIds, setSeenIds] = useLocalStorage("notifications:seen-ids", [])

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/notifications")
      if (!response.ok) return
      const data = await response.json()
      setTopics(data.notifications ?? [])
    } catch {
      // best-effort, same as existing Notifications component
    }
  }, [])

  useEffect(() => {
    refresh()
    window.addEventListener("notifications:refresh", refresh)
    return () => window.removeEventListener("notifications:refresh", refresh)
  }, [refresh])

  const activeTopic = topics.find(t => isActive(t.status)) ?? null

  // Poll the list while a job is active so the ProgressCircle reflects real progress.
  // Mirrors the interval used by useTopicUpdater in the notifications engine.
  useEffect(() => {
    if (!activeTopic) return

    let timeoutId
    let cancelled = false

    const poll = async () => {
      await refresh()
      if (!cancelled) {
        timeoutId = window.setTimeout(poll, POLLING_INTERVAL_MS)
      }
    }

    timeoutId = window.setTimeout(poll, POLLING_INTERVAL_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [activeTopic, refresh])

  const completedTopics = topics.filter(t => !isActive(t.status))
  const progress = activeTopic?.progress
    ? activeTopic.progress.current / activeTopic.progress.total
    : 0
  const unseenCount = completedTopics.filter(t => !seenIds.includes(t.id)).length

  const markSeen = useCallback(() => {
    setSeenIds(prev => [...new Set([...prev, ...completedTopics.map(t => t.id)])])
  }, [completedTopics, setSeenIds])

  return { topics, activeTopic, progress, unseenCount, hasTopics: topics.length > 0, markSeen }
}
